import React, { useMemo, useState } from 'react';
import { CheckCircle, Lock, Search, Shield, XCircle } from 'lucide-react';
import PermissionCard from '../components/PermissionCard';
import { permissions as permissionsApi } from '../utils/api';

/** Purpose: Normalizes values for case-insensitive matching. Inputs: value. Output: normalized string. */
function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

const defaultGrantForm = {
  providerId: '',
  purpose: '',
  expiryDate: '',
  allRecords: true,
  recordIds: []
};

/** Purpose: Renders permissions management view. Inputs: { records, providers, permissions, onRevokePermission, onGrantPermission }. Output: React render output (JSX). */
export default function Permissions({ records, providers, permissions, onRevokePermission, onGrantPermission }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [grantForm, setGrantForm] = useState(defaultGrantForm);
  const [grantError, setGrantError] = useState('');
  const [isSavingPermission, setIsSavingPermission] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfPermission, setPdfPermission] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfFileName, setPdfFileName] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');

  const providerOptions = useMemo(() => {
    return providers || [];
  }, [providers]);

  const providerLabelById = useMemo(() => {
    const labels = new Map();
    for (const provider of providerOptions) {
      labels.set(provider.id, `${provider.name} - ${provider.organization}`);
    }
    return labels;
  }, [providerOptions]);

  const providerById = useMemo(() => {
    return new Map(providerOptions.map((provider) => [provider.id, provider]));
  }, [providerOptions]);

  const filteredPermissions = useMemo(() => {
    return permissions.filter((permission) => {
      const matchesSearch = searchQuery === ''
        || normalize(permission.grantedTo).includes(normalize(searchQuery))
        || normalize(permission.organization).includes(normalize(searchQuery))
        || normalize(permission.purpose).includes(normalize(searchQuery));
      const matchesProvider = providerFilter === 'all' || permission.provider?.id === providerFilter;
      const matchesStatus = statusFilter === 'all' || permission.status === statusFilter;
      return matchesSearch && matchesProvider && matchesStatus;
    });
  }, [permissions, searchQuery, providerFilter, statusFilter]);

  const activePermissions = filteredPermissions.filter((permission) => permission.status === 'active');
  const inactivePermissions = filteredPermissions.filter((permission) => permission.status !== 'active');

  /** Purpose: Toggles record selection in create-authorization modal. Inputs: recordId. Output: none. */
  const toggleGrantRecord = (recordId) => {
    setGrantForm((previous) => ({
      ...previous,
      recordIds: previous.recordIds.includes(recordId)
        ? previous.recordIds.filter((id) => id !== recordId)
        : [...previous.recordIds, recordId]
    }));
  };

  /** Purpose: Submits create-authorization modal to backend. Inputs: event. Output: Promise with operation result. */
  const handleGrantSubmit = async (event) => {
    event.preventDefault();
    if (!grantForm.providerId || !grantForm.purpose || !grantForm.expiryDate) {
      setGrantError('Select provider, purpose, and expiry date.');
      return;
    }
    if (!grantForm.allRecords && grantForm.recordIds.length === 0) {
      setGrantError('Select one or more records, or choose all records.');
      return;
    }

    setIsSavingPermission(true);
    setGrantError('');

    try {
      const provider = providerById.get(grantForm.providerId);
      await onGrantPermission({
        providerId: grantForm.providerId,
        grantedTo: provider?.name,
        organization: provider?.organization,
        purpose: grantForm.purpose,
        expiryDate: grantForm.expiryDate,
        accessLevel: 'READ',
        allRecords: grantForm.allRecords,
        recordIds: grantForm.allRecords ? [] : grantForm.recordIds
      });
      setGrantForm(defaultGrantForm);
      setShowCreateModal(false);
    } catch (error) {
      setGrantError(error.message || 'Failed to create authorization.');
    } finally {
      setIsSavingPermission(false);
    }
  };

  /** Purpose: Releases currently loaded PDF object URL. Inputs: none. Output: none. */
  const releasePdfUrl = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
  };

  /** Purpose: Opens in-app authorization PDF preview modal. Inputs: permission. Output: Promise with operation result. */
  const handleViewPdf = async (permission) => {
    releasePdfUrl();
    setPdfPermission(permission);
    setPdfFileName('');
    setPdfUrl('');
    setPdfError('');
    setPdfLoading(true);
    setShowPdfModal(true);

    try {
      const response = await permissionsApi.getPdf(permission.id);
      const objectUrl = window.URL.createObjectURL(response.blob);
      setPdfUrl(objectUrl);
      setPdfFileName(response.fileName || `authorization-${permission.id}.pdf`);
    } catch (error) {
      setPdfError(error.message || 'Failed to load authorization PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  /** Purpose: Closes in-app authorization PDF modal and clears resources. Inputs: none. Output: none. */
  const closePdfModal = () => {
    releasePdfUrl();
    setShowPdfModal(false);
    setPdfPermission(null);
    setPdfFileName('');
    setPdfUrl('');
    setPdfError('');
    setPdfLoading(false);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="bg-white rounded-2xl border border-slate-200/60 p-4 sm:p-6 shadow-card flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">Permissions</h2>
          <p className="text-sm text-slate-400 mt-1">
            Manage HIPAA authorizations and revoke access when needed.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setGrantError('');
            setGrantForm(defaultGrantForm);
            setShowCreateModal(true);
          }}
          className="w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl text-sm font-medium hover:shadow-md disabled:opacity-50 transition-all"
          disabled={providerOptions.length === 0}
        >
          Create Authorization
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200/60 p-4 shadow-card space-y-3">
        <div className="relative group">
          <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 group-focus-within:text-brand-500 transition-colors" />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search by provider, organization, or purpose..."
            className="w-full pl-11 pr-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm transition-all"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <select
            value={providerFilter}
            onChange={(event) => setProviderFilter(event.target.value)}
            className="px-4 py-2.5 bg-slate-50/80 border border-slate-200 rounded-xl text-sm appearance-none transition-all"
          >
            <option value="all">All Providers</option>
            {providerOptions.map((provider) => (
              <option key={provider.id} value={provider.id}>
                {providerLabelById.get(provider.id)}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="px-4 py-2.5 bg-slate-50/80 border border-slate-200 rounded-xl text-sm appearance-none transition-all"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
          </select>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2 tracking-tight">
          <CheckCircle className="w-4 h-4 text-emerald-500" />
          Active Permissions ({activePermissions.length})
        </h3>
        {activePermissions.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200/60 p-6 text-sm text-slate-400">
            No active permissions in this filter.
          </div>
        ) : (
          activePermissions.map((permission) => (
            <PermissionCard
              key={permission.id}
              permission={permission}
              records={records}
              onRevoke={onRevokePermission}
              onViewPdf={handleViewPdf}
            />
          ))
        )}
      </div>

      <div className="space-y-4">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2 tracking-tight">
          <XCircle className="w-4 h-4 text-slate-400" />
          Revoked / Expired ({inactivePermissions.length})
        </h3>
        {inactivePermissions.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200/60 p-6 text-sm text-slate-400">
            No revoked or expired permissions in this filter.
          </div>
        ) : (
          inactivePermissions.map((permission) => (
            <PermissionCard
              key={permission.id}
              permission={permission}
              records={records}
              onRevoke={onRevokePermission}
              onViewPdf={handleViewPdf}
              isExpired
            />
          ))
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900 flex items-center gap-2">
        <Lock className="w-4 h-4" />
        Revoking a permission triggers provider notification when provider email is configured.
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={handleGrantSubmit} className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-4 sm:p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-lg font-semibold text-slate-900">Create New Authorization</h3>
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setGrantError('');
                }}
                className="text-slate-500 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            {grantError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                {grantError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Provider *</label>
                <select
                  value={grantForm.providerId}
                  onChange={(event) => setGrantForm((previous) => ({ ...previous, providerId: event.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  <option value="">Select provider</option>
                  {providerOptions.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name} - {provider.organization}
                    </option>
                  ))}
                </select>
              </div>
              <input
                type="text"
                placeholder="Purpose *"
                value={grantForm.purpose}
                onChange={(event) => setGrantForm((previous) => ({ ...previous, purpose: event.target.value }))}
                className="px-3 py-2 border border-slate-300 rounded-lg md:col-span-2"
              />
              <input
                type="date"
                value={grantForm.expiryDate}
                onChange={(event) => setGrantForm((previous) => ({ ...previous, expiryDate: event.target.value }))}
                min={new Date().toISOString().split('T')[0]}
                className="px-3 py-2 border border-slate-300 rounded-lg"
              />
              <div className="px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-700 text-sm">
                Read Only
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-slate-700 mb-3">
                <input
                  type="checkbox"
                  checked={grantForm.allRecords}
                  onChange={(event) =>
                    setGrantForm((previous) => ({
                      ...previous,
                      allRecords: event.target.checked,
                      recordIds: event.target.checked ? [] : previous.recordIds
                    }))
                  }
                />
                Blanket HIPAA authorization (all current records)
              </label>
              <p className="text-sm font-medium text-slate-700 mb-2">
                {grantForm.allRecords
                  ? `All records authorized (${records.length} currently available)`
                  : `Select Records (${grantForm.recordIds.length} selected)`}
              </p>
              {grantForm.allRecords ? (
                <div className="border border-slate-200 rounded-lg p-3 text-sm text-slate-600 bg-slate-50">
                  This authorization applies to all records currently in your account.
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto">
                  {records.map((record) => (
                    <label key={record.id} className="flex items-start gap-3 px-3 py-2 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={grantForm.recordIds.includes(record.id)}
                        onChange={() => toggleGrantRecord(record.id)}
                        className="mt-1"
                      />
                      <div>
                        <p className="text-sm font-medium text-slate-900">{record.title}</p>
                        <p className="text-xs text-slate-500">
                          {record.type} • {new Date(record.date).toLocaleDateString()}{record.provider ? ` • ${record.provider}` : ''}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Revoking this authorization later will send provider notification when provider email is available.
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setGrantError('');
                }}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSavingPermission}
                className="px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50"
              >
                {isSavingPermission ? 'Saving...' : 'Create Authorization'}
              </button>
            </div>
          </form>
        </div>
      )}

      {showPdfModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[92vh] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Authorization PDF</h3>
                <p className="text-sm text-slate-500">{pdfPermission?.grantedTo || 'Authorization'}</p>
              </div>
              <div className="flex items-center gap-2">
                {pdfUrl && (
                  <button
                    type="button"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = pdfUrl;
                      link.download = pdfFileName || `authorization-${pdfPermission?.id || 'file'}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                    }}
                    className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50"
                  >
                    Download
                  </button>
                )}
                <button
                  type="button"
                  onClick={closePdfModal}
                  className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="p-4 bg-slate-50 max-h-[calc(92vh-68px)] overflow-y-auto">
              {pdfLoading && (
                <div className="bg-white border border-slate-200 rounded-lg p-6 text-sm text-slate-600">
                  Loading authorization PDF...
                </div>
              )}
              {!pdfLoading && pdfError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {pdfError}
                </div>
              )}
              {!pdfLoading && !pdfError && pdfUrl && (
                <iframe
                  title="Authorization PDF Preview"
                  src={pdfUrl}
                  className="w-full h-[78vh] bg-white border border-slate-200 rounded-lg"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
